import {
  GoogleIcon,
  MetaIcon,
  TvIcon,
  HierarchyCircle03Icon,
  AudioWave01Icon,
  Image03Icon,
  ShoppingCart01Icon,
  Shield01Icon,
  MagicWand01Icon,
  BalanceScaleIcon,
  FilterIcon,
} from '@hugeicons-pro/core-stroke-rounded'
import type { IconSvgElement } from '@hugeicons/react'
import type { ChannelKey } from '@/lib/connectors/catalog'
import type { MarketplaceCommandCategory } from '@/lib/marketplace/queries'

// =============================================================================
// Marketplace tokens — bespoke per-card colors for the v3 design.
//
// These are surface-specific and intentionally do NOT map to atrium-* design
// tokens — the marketplace is the only place that uses these pastels,
// command tints, and hero gradients. Values come from
// marketplace-claude-code-spec.md §8.
//
// =============================================================================

export interface ChannelPalette {
  bg: string
  ink: string
  eyebrow: string
  markBg: string
  markInk: string
}

export interface CommandPalette {
  bg: string
  ink: string
  cta: string
}

export interface FeatureGradient {
  gradient: string
  textTone: 'light' | 'dark'
  ink?: string
  meta?: string
  decos?: Array<{
    top?: string
    right?: string
    bottom?: string
    left?: string
    size: string
    color: string
  }>
}

// -- Raw palettes ------------------------------------------------------------
// The eight channel pastels and four command tints from the spec. Kept as
// named exports so they remain a single source of truth even though the
// decorator map below is what the components actually consume.

export const channelPalettes = {
  googleSearch: {
    bg: '#FFE8A3',
    ink: '#1A1A18',
    eyebrow: '#7A5C00',
    markBg: '#1A1A18',
    markInk: '#FFE8A3',
  },
  metaAds: {
    bg: '#A6E1FF',
    ink: '#0A3A5C',
    eyebrow: '#0A3A5C',
    markBg: '#0A3A5C',
    markInk: '#A6E1FF',
  },
  localBroadcast: {
    bg: '#D4C7FF',
    ink: '#2D1B5E',
    eyebrow: '#2D1B5E',
    markBg: '#2D1B5E',
    markInk: '#D4C7FF',
  },
  premiumCtv: {
    bg: '#FFD4D4',
    ink: '#5C0F0F',
    eyebrow: '#5C0F0F',
    markBg: '#5C0F0F',
    markInk: '#FFD4D4',
  },
  dspNetwork: {
    bg: '#C7F5DD',
    ink: '#0A3D29',
    eyebrow: '#0A3D29',
    markBg: '#0A3D29',
    markInk: '#C7F5DD',
  },
  streamingAudio: {
    bg: '#FFE0B5',
    ink: '#5C3300',
    eyebrow: '#5C3300',
    markBg: '#5C3300',
    markInk: '#FFE0B5',
  },
  retailMedia: {
    bg: '#FFE5F0',
    ink: '#5C0F33',
    eyebrow: '#5C0F33',
    markBg: '#5C0F33',
    markInk: '#FFE5F0',
  },
  outOfHome: {
    bg: '#E0EAFF',
    ink: '#1A2D5C',
    eyebrow: '#1A2D5C',
    markBg: '#1A2D5C',
    markInk: '#E0EAFF',
  },
} as const satisfies Record<string, ChannelPalette>

export const commandPalettes = {
  fraudSweep: { bg: '#FF6B9D', ink: '#5C0F33', cta: '#5C0F33' },
  creativeRefresh: { bg: '#FFB84D', ink: '#663300', cta: '#663300' },
  budgetRebalance: { bg: '#00D67E', ink: '#0A3D29', cta: '#0A3D29' },
  negativeKeywordAudit: { bg: '#7B6BFF', ink: '#FFFFFF', cta: '#1F1654' },
} as const satisfies Record<string, CommandPalette>

// -- Channel decorator lookup (the "one place" the rest of the UI reads from) ---
//
// To add or change how a channel renders in the rail, edit this object — it's
// the single source of truth for per-channel palette, icon, and eyebrow.
// Channels in `channel_catalog` whose `key` is NOT in this map fall back to
// `neutralChannelDecorator(accent_hex)` so adding a new channel.key in the
// DB doesn't require a code change to show up — it just appears with a
// neutral skin until someone tunes it here.
//
// Coverage today (must stay in sync with `CHANNEL_KEYS` in
// lib/connectors/catalog.ts):
//   tuned:   search, social, tv, programmatic, audio, retail
//   neutral: (none — every current ChannelKey is tuned)
//
// `premiumCtv` and `outOfHome` palettes are intentionally unused at the
// channel level today (held for a future per-subchannel skin).

export interface ChannelDecorator {
  palette: ChannelPalette
  icon: IconSvgElement
}

const CHANNEL_DECORATORS: Partial<Record<ChannelKey, ChannelDecorator>> = {
  search: { palette: channelPalettes.googleSearch, icon: GoogleIcon },
  social: { palette: channelPalettes.metaAds, icon: MetaIcon },
  tv: { palette: channelPalettes.localBroadcast, icon: TvIcon },
  programmatic: {
    palette: channelPalettes.dspNetwork,
    icon: Image03Icon,
  },
  audio: { palette: channelPalettes.streamingAudio, icon: AudioWave01Icon },
  retail: { palette: channelPalettes.retailMedia, icon: ShoppingCart01Icon },
}

function neutralPaletteFor(accentHex: string): ChannelPalette {
  // Soft cream surface with the channel's own accent_hex as ink + mark color.
  // Keeps unbranded channels readable while preserving identity.
  return {
    bg: '#F4F2EC',
    ink: accentHex,
    eyebrow: accentHex,
    markBg: accentHex,
    markInk: '#FFFFFF',
  }
}

export function channelDecorator(
  channelKey: ChannelKey,
  accentHex: string,
): ChannelDecorator {
  const tuned = CHANNEL_DECORATORS[channelKey]
  if (tuned) return tuned
  return {
    palette: neutralPaletteFor(accentHex),
    icon: HierarchyCircle03Icon,
  }
}

// -- Command decorator lookup -------------------------------------------------
//
// Commands map by `category` (a closed enum in the schema) so every category
// is guaranteed to render with a tuned skin. Adding a new category requires
// both a schema migration AND a new entry here — the typed Record forces it.

export interface CommandDecorator {
  palette: CommandPalette
  icon: IconSvgElement
  eyebrow: string
}

export const COMMAND_DECORATORS: Record<
  MarketplaceCommandCategory,
  CommandDecorator
> = {
  cleanup: {
    palette: commandPalettes.fraudSweep,
    icon: Shield01Icon,
    eyebrow: 'Cleanup',
  },
  creative: {
    palette: commandPalettes.creativeRefresh,
    icon: MagicWand01Icon,
    eyebrow: 'Creative',
  },
  optimization: {
    palette: commandPalettes.budgetRebalance,
    icon: BalanceScaleIcon,
    eyebrow: 'Optimize',
  },
  intelligence: {
    palette: commandPalettes.negativeKeywordAudit,
    icon: FilterIcon,
    eyebrow: 'Intelligence',
  },
}

// -- Feature (hero) gradients ------------------------------------------------
// Three gradient hero cards. These are fixture-only — there's no DB analog
// today for "Featured this week" content.

export const featureGradients: Record<
  'streaming' | 'linearTv' | 'search',
  FeatureGradient
> = {
  streaming: {
    gradient: 'linear-gradient(135deg,#FF4E2C 0%,#FF8A3D 100%)',
    textTone: 'light',
    decos: [
      { top: '-50px', right: '-50px', size: '220px', color: 'rgba(255,255,255,0.10)' },
      { bottom: '-70px', left: '-40px', size: '180px', color: 'rgba(0,0,0,0.08)' },
    ],
  },
  linearTv: {
    gradient: 'linear-gradient(135deg,#2D1B5E 0%,#7B6BFF 100%)',
    textTone: 'light',
    decos: [
      { top: '-40px', left: '-40px', size: '200px', color: 'rgba(255,255,255,0.08)' },
    ],
  },
  search: {
    gradient: 'linear-gradient(135deg,#FFE8A3 0%,#FFB84D 100%)',
    textTone: 'dark',
    ink: '#3D2D00',
    meta: '#5C4400',
    decos: [
      { bottom: '-40px', right: '-40px', size: '200px', color: 'rgba(255,255,255,0.40)' },
    ],
  },
}

export type FeatureGradientKey = keyof typeof featureGradients

// -- Shared layout insets ----------------------------------------------------
// The left content inset (top-bar, hero, axis pills, rail titles, AND the
// first card in every rail) and the rail's right bleed. One knob.

export const layoutInsets = {
  left: '2.75rem', // 44px
  right: '1.5rem', // 24px
} as const
